import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import JwalekLogo from '@/components/JwalekLogo';
import SocialLoginButtons from '@/components/SocialLoginButtons';
import SEO from '@/components/SEO';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t } = useLanguage();
  const nav = useNavigate();
  const loc = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(t('login.welcomeToast', 'Welcome back!'));
      nav(loc.state?.from?.pathname || (user.role === 'admin' ? '/admin' : '/'), { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('login.failed', 'Login failed'));
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-16">
      <SEO title="Sign in" description="Sign in to your Jwalek account to design and order custom prints." path="/login" noindex />
      <Card className="jw-card p-8">
        <div className="text-center">
          <JwalekLogo size={32} />
          <h1 className="font-display text-2xl font-bold mt-4">{t('login.welcomeBack', 'Welcome back')}</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--jw-text-muted)' }}>{t('login.signInJwalek', 'Sign in to your Jwalek account')}</p>
        </div>

        <div className="mt-6"><SocialLoginButtons /></div>

        <div className="my-6 flex items-center gap-3" style={{ color: 'var(--jw-text-subtle)' }}>
          <div className="flex-1 h-px" style={{ background: 'var(--jw-border)' }} />
          <span className="text-xs uppercase tracking-wider">{t('login.orWithEmail', 'or with email')}</span>
          <div className="flex-1 h-px" style={{ background: 'var(--jw-border)' }} />
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-sm" style={{ color: 'var(--jw-text-muted)' }}>{t('contact.email', 'Email')}</label>
            <Input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1" />
          </div>
          <div>
            <label className="text-sm" style={{ color: 'var(--jw-text-muted)' }}>{t('login.password', 'Password')}</label>
            <Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1" />
          </div>
          <Button type="submit" disabled={loading} data-testid="login-submit-button" className="jw-btn-glossy w-full rounded-[14px] h-11">
            {loading ? t('login.signingIn', 'Signing in...') : t('nav.signIn', 'Sign in')}
          </Button>
        </form>
        <p className="text-center text-sm mt-6" style={{ color: 'var(--jw-text-muted)' }}>
          {t('login.noAccount', 'No account?')} <Link to="/register" data-testid="login-register-link" className="hover:underline" style={{ color: 'var(--jw-text)' }}>{t('login.createOne', 'Create one')}</Link>
        </p>
      </Card>
    </div>
  );
};

export default Login;
